import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../services/api";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { RedemptionHistory } from "../components/gift-cards";

type HistoryTab = "funded" | "responses" | "redemptions";

export default function ProfilePage() {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState<HistoryTab>("funded");

  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const response = await api.get("/users/me");
      return response.data;
    },
  });

  const { data: fundedData, isLoading: fundedLoading } = useQuery({
    queryKey: ["history", "funded"],
    queryFn: async () => {
      const response = await api.get("/users/me/history/funding");
      return response.data;
    },
    enabled: activeTab === "funded",
  });

  const { data: responsesData, isLoading: responsesLoading } = useQuery({
    queryKey: ["history", "responses"],
    queryFn: async () => {
      const response = await api.get("/users/me/history/responses");
      return response.data;
    },
    enabled: activeTab === "responses",
  });

  const user = profileData?.data.user;

  useEffect(() => {
    if (user?.profile) {
      setDisplayName(user.profile.display_name || "");
      setAvatarUrl(user.profile.avatar_url || "");
    }
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: async (updates: { display_name?: string; avatar_url?: string }) => {
      const response = await api.patch("/users/me/profile", updates);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setMessage("Profile updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.error || "Failed to update profile");
    },
  });

  if (profileLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading profile...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="card">
        <p className="text-gray-400">Profile not found.</p>
      </div>
    );
  }

  const isSuccess = message.includes('success') || message.includes('successfully');

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-100 mb-8">Profile</h1>

      {message && (
        <div className={`mb-4 p-4 rounded-lg border ${isSuccess ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
          {message}
        </div>
      )}

      {/* Account Information */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Account Information</h2>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-400">Email: </span>
            <span className="text-gray-200">{user.email}</span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-400">Role: </span>
            <span className="text-gray-200 capitalize">{user.role.replace("_", " ")}</span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-400">Status: </span>
            <span className="text-gray-200 capitalize">{user.status}</span>
          </div>
        </div>
      </div>

      {/* Edit Profile */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Edit Profile</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateMutation.mutate({ display_name: displayName, avatar_url: avatarUrl || undefined });
          }}
          className="space-y-4 max-w-md"
        >
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="avatarUrl" className="block text-sm font-medium text-gray-300 mb-2">
              Avatar URL
            </label>
            <input
              id="avatarUrl"
              type="url"
              className="input"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://example.com/avatar.png"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </div>

      {/* History Section */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Activity History</h2>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-dark-border mb-6">
          <button
            onClick={() => setActiveTab("funded")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "funded"
                ? "text-primary-300 border-b-2 border-primary-300"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            Funded
          </button>
          <button
            onClick={() => setActiveTab("responses")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "responses"
                ? "text-primary-300 border-b-2 border-primary-300"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            Responses
          </button>
          <button
            onClick={() => setActiveTab("redemptions")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "redemptions"
                ? "text-primary-300 border-b-2 border-primary-300"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            Redemptions
          </button>
        </div>

        {/* Tab Content */}
        <div className="min-h-[300px]">
          {activeTab === "funded" && (
            <FundedSurveysList data={fundedData?.data} isLoading={fundedLoading} />
          )}
          {activeTab === "responses" && (
            <ResponsesList data={responsesData?.data} isLoading={responsesLoading} />
          )}
          {activeTab === "redemptions" && (
            <RedemptionHistory />
          )}
        </div>
      </div>
    </div>
  );
}

// Helper Components
function FundedSurveysList({ data, isLoading }: { data: any; isLoading: boolean }) {
  if (isLoading) return <div className="text-gray-400">Loading...</div>;
  if (!data?.funded_surveys || data.funded_surveys.length === 0) {
    return <div className="text-gray-400">No surveys funded yet.</div>;
  }

  return (
    <div className="space-y-3">
      {data.funded_surveys.map((survey: any) => (
        <div key={survey.id} className="bg-dark-800 p-4 rounded-lg border border-dark-border">
          <div className="flex justify-between items-start">
            <div>
              <Link to={`/surveys/${survey.id}`} className="text-primary-300 hover:text-primary-200 font-medium">
                {survey.title}
              </Link>
              <p className="text-sm text-gray-400 mt-1">
                Status: <span className={`capitalize ${
                  survey.status === "active" ? "text-green-400" :
                  survey.status === "completed" ? "text-blue-400" :
                  "text-gray-400"
                }`}>{survey.status}</span>
              </p>
              {survey.funded_at && (
                <p className="text-sm text-gray-500 mt-1">
                  Funded: {new Date(survey.funded_at).toLocaleDateString()}
                </p>
              )}
            </div>
            {(survey.budget && parseFloat(survey.budget) > 0) ? (
              <div className="text-right">
                <p className="text-sm font-medium text-gray-300">{survey.budget} points</p>
                <p className="text-xs text-gray-500">Funded</p>
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResponsesList({ data, isLoading }: { data: any; isLoading: boolean }) {
  if (isLoading) return <div className="text-gray-400">Loading...</div>;
  if (!data?.responses || data.responses.length === 0) {
    return <div className="text-gray-400">No responses yet. Vote on surveys to see your voting history here.</div>;
  }

  return (
    <div className="space-y-3">
      {data.responses.map((response: any) => (
        <div key={response.survey_id} className="bg-dark-800 p-4 rounded-lg border border-dark-border">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <Link to={`/surveys/${response.survey_id}`} className="text-primary-300 hover:text-primary-200 font-medium">
                {response.survey_title}
              </Link>
              <p className="text-sm text-gray-400 mt-1">
                <span className="text-gray-200">Voted for: </span>
                <span className="text-gray-100 font-medium">{response.selected_option_text}</span>
              </p>
              <div className="flex items-center gap-2 mt-1">
                {response.reward_claimed ? (
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Reward claimed</span>
                ) : response.reward_eligible ? (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">Eligible for reward</span>
                ) : null}
              </div>
              <div className="mt-2 space-y-1">
                <p className="text-sm text-gray-500">
                  Voted: {new Date(response.submitted_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            {(response.reward_amount && parseFloat(response.reward_amount) > 0) ? (
              <div className="text-right ml-4">
                <p className="text-sm font-medium text-green-400">{response.reward_amount} points</p>
                <p className="text-xs text-gray-500">Reward</p>
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
